import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import customerCareIcon from "@/assets/customer-care-icon.png";

const ProductBoumatic = () => {
  return (
    <>
      <Helmet>
        <title>BouMatic - La technologie de traite innovante pour améliorer la vie de votre entreprise laitière</title>
        <meta 
          name="description" 
          content="BouMatic, partenaire de votre réussite depuis plus d'un siècle, développe des solutions de traite innovantes pour maximiser le rendement de votre entreprise laitière." 
        />
      </Helmet>

      <div className="-mx-4 -mt-16">
        {/* Hero Section */}
        <section 
          className="relative bg-cover bg-center text-white py-32"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1500595046743-cd271d694d30?q=80&w=2074)',
            minHeight: '500px'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <div className="w-16 h-1 bg-accent mb-6"></div>
              <h1 className="text-3xl md:text-5xl font-bold mb-6">
                BouMatic: La technologie de traite innovante pour améliorer la vie de votre entreprise laitière
              </h1>
              <p className="text-lg leading-relaxed mb-4">
                BouMatic, partenaire de votre réussite depuis plus d'un siècle, développe des solutions de traite innovantes pour maximiser le rendement de votre entreprise laitière. En associant technologie de pointe et respect de l'animal, nous vous proposons une approche plus naturelle et productive de la traite pour maximiser le rendement de votre entreprise.
              </p>
              <p className="text-lg leading-relaxed">
                Chez Agro, nous sommes fiers de vous fournir la gamme de produits BouMatic depuis 1993, conçus pour harmoniser passion et efficacité. Adoptez BouMatic, et alliez productivité, équilibre de vie et préparation de l'avenir de votre exploitation.
              </p>
            </div>
          </div>
        </section>

        {/* Logo Section */}
        <section className="bg-white py-12 md:py-16">
          <div className="container mx-auto px-4 text-center">
            <img 
              src="https://agroref.ca/wp-content/uploads/2023/03/logo-Bm-sans-fond-1-1.png" 
              alt="BouMatic - Pour La Vie De Votre Laiterie"
              className="mx-auto max-w-md h-auto"
            />
          </div>
        </section>

        {/* Pourquoi choisir BouMatic */}
        <section className="bg-gray-50 py-12 md:py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Pourquoi choisir BouMatic?
              </h2>
              <p className="text-lg text-muted-foreground">
                Les avantages indéniables d'une technologie de traite éprouvée
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Innovation technologique */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Innovation technologique</h3>
                <p className="text-muted-foreground leading-relaxed">
                  BouMatic utilise une technologie de pointe pour optimiser les processus de traite. Leurs salle de traite de tous formats, alliant robustesse et simplicité, maximisent l'efficacité tout en réduisant le temps passé à traire.
                </p>
              </div>

              {/* Efficacité de la récolte du lait */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Efficacité de la récolte du lait</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Les équipements BouMatic, des griffes aux manchons trayeurs, garantissent une collecte du lait rapide et sûre, pour une production optimale.
                </p>
              </div>

              {/* Refroidissement performant */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Refroidissement performant</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Grâce aux systèmes de refroidissement BouMatic, le lait est conservé à une température idéale, pour une qualité et une durée de conservation optimales.
                </p>
              </div>

              {/* Confort des animaux */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Confort des animaux</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Avec BouMatic, le bien-être des vaches est une priorité. Leurs équipements de traite sont fabriqués sur mesure pour une traite douce, complète et confortable.
                </p>
              </div>

              {/* Durabilité et maintenance */}
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <div className="mb-4">
                  <CheckCircle2 className="w-12 h-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-4">Durabilité et maintenance</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Les produits BouMatic sont conçus pour durer. En cas de besoin, les pièces de rechange et le service de maintenance de BouMatic sont à votre disposition pour assurer la longévité de vos équipements.
                </p>
              </div>

              {/* En savoir plus */}
              <div className="bg-accent/10 p-8 rounded-lg shadow-sm flex flex-col justify-center items-center text-center">
                <h3 className="text-xl font-bold mb-4">En savoir plus</h3>
                <p className="text-muted-foreground mb-6">
                  Pour en savoir plus, visitez le site web
                </p>
                <Button 
                  asChild 
                  variant="outline" 
                  className="border-accent text-accent hover:bg-accent hover:text-white"
                >
                  <a 
                    href="https://boumatic.com/ca_fr/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    www.boumatic.com
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative bg-[#333333] text-white py-12 md:py-16 overflow-hidden">
          <img src={customerCareIcon} alt="" className="absolute right-8 top-1/2 -translate-y-1/2 w-64 h-64 opacity-10 object-contain pointer-events-none" />
          <div className="container mx-auto px-4 text-center relative z-10">
            <p className="text-sm uppercase tracking-wider mb-4">CONTACTEZ-NOUS</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à Optimiser Votre Production Laitière?
            </h2>
            <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
              Exploitez pleinement le potentiel de votre production laitière avec Agro. Contactez-nous dès aujourd&apos;hui pour découvrir comment
            </p>
            <p className="text-base md:text-lg mb-2 max-w-4xl mx-auto leading-relaxed">
              nos services et produits peuvent aider à améliorer l&apos;efficacité de votre exploitation et garantir la meilleure qualité de lait possible.
            </p>
            <p className="text-base md:text-lg mb-8 max-w-4xl mx-auto leading-relaxed">
              Nous sommes disponibles 24/7 pour vous assister dans vos urgences.
            </p>
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-white">
              <Link to="/contact">Contactez-nous dès maintenant!</Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductBoumatic;
